import React from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, User, Menu } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { base64ToFile } from "../../utils/util";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { stringToColor } from "../member/tierConfig";
import useMediaQuery from "../shared/useMediaQuery";

const ROLE_LABELS = {
  SUPERADMIN: "Super Admin",
  MANAGER: "Manager",
  VENDOR: "Vendor",
  WAREHOUSE_ADMIN: "Warehouse Admin",
  WAREHOUSE_STAFF: "Warehouse Staff",
};

// Same route-availability constraint as ManagementProfileCard — only
// SUPERADMIN/MANAGER have a /dashboard/my-profile route registered in App.js.
const PROFILE_ROUTE_ROLES = new Set(["SUPERADMIN", "MANAGER"]);

export default function ManagementTopBar({ onOpenSidebar }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const user = getUsers();

  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`;
  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "";
  const canOpenProfile = PROFILE_ROUTE_ROLES.has(user?.role);

  let avatarSrc = null;
  if (user?.profile_picture) {
    try {
      avatarSrc = URL.createObjectURL(
        base64ToFile(user.profile_picture, "avatar", "image/*")
      );
    } catch {
      avatarSrc = null;
    }
  }

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    const logoutChannel = new BroadcastChannel("logout_channel");
    logoutChannel.postMessage("logout");
    navigate("/");
  };

  return (
    <header
      className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 md:tw-px-6 tw-h-14 tw-border-b tw-border-white/[0.06] tw-bg-black/70 tw-backdrop-blur-md tw-sticky tw-top-0 tw-z-10"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: isMobile ? "0 14px" : "0 24px",
        height: 56,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      {onOpenSidebar ? (
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Open navigation"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.04)",
            color: "#22d3ee",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Menu size={17} strokeWidth={2.2} />
        </button>
      ) : (
        <div className="tw-flex-1" style={{ flex: 1 }} />
      )}

      {/* Right: role badge + avatar */}
      <div className="tw-flex tw-items-center" style={{ display: "flex", alignItems: "center", minWidth: 0, marginLeft: isMobile ? "auto" : 0 }}>
        {/* Role badge (desktop only) */}
        <div
          className="tw-hidden md:tw-flex tw-items-center tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-text-[12px] tw-font-bold"
          style={{
            alignItems: "center",
            marginRight: 10,
            padding: "6px 12px",
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 700,
            borderColor: "rgba(0,173,181,0.3)",
            borderWidth: 1,
            borderStyle: "solid",
            color: "#00ADB5",
          }}
        >
          <span>{roleLabel}</span>
        </div>

        {/* Avatar dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="tw-relative tw-w-8 tw-h-8 tw-rounded-full tw-overflow-hidden tw-cursor-pointer tw-border-2 tw-border-white/10 hover:tw-border-white/20 tw-transition-colors tw-bg-transparent tw-p-0"
              style={{
                position: "relative",
                width: 32,
                height: 32,
                borderRadius: "50%",
                overflow: "hidden",
                cursor: "pointer",
                border: "2px solid rgba(255,255,255,0.1)",
                background: "transparent",
                padding: 0,
              }}
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={name}
                  className="tw-w-full tw-h-full tw-object-cover"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div
                  className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-text-white"
                  style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: stringToColor(name || "User") }}
                >
                  {initials}
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tw-w-56">
            <DropdownMenuLabel>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{name}</span>
                <span style={{ fontSize: 12, color: "#c3c8cc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {canOpenProfile && (
              <>
                <DropdownMenuItem onClick={() => navigate("/dashboard/my-profile")}>
                  <User size={14} style={{ marginRight: 8 }} />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={handleLogout}
              className="tw-text-bad focus:tw-text-bad"
              style={{ color: "#f27059" }}
            >
              <LogOut size={14} style={{ marginRight: 8 }} />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
